import { useEffect, useRef } from "react";
import type { ChangeEvent } from "react";
import { track } from "../analytics";

export interface FilterState {
  minPriceUsd: number | null;
  maxPriceUsd: number | null;
  hideClosed: boolean;
}

export const DEFAULT_FILTERS: FilterState = {
  minPriceUsd: null,
  maxPriceUsd: null,
  hideClosed: false,
};

interface Props {
  value: FilterState;
  onChange: (next: FilterState) => void;
}

const PRICE_TRACK_DEBOUNCE_MS = 600;

function parseNum(raw: string): number | null {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function Filters({ value, onChange }: Props) {
  const trackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
  }, []);

  const schedulePriceTrack = (next: FilterState) => {
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    trackTimerRef.current = setTimeout(() => {
      track("filter_price", {
        minUsd: next.minPriceUsd,
        maxUsd: next.maxPriceUsd,
      });
    }, PRICE_TRACK_DEBOUNCE_MS);
  };

  const onMin = (e: ChangeEvent<HTMLInputElement>) => {
    const next = { ...value, minPriceUsd: parseNum(e.target.value) };
    onChange(next);
    schedulePriceTrack(next);
  };
  const onMax = (e: ChangeEvent<HTMLInputElement>) => {
    const next = { ...value, maxPriceUsd: parseNum(e.target.value) };
    onChange(next);
    schedulePriceTrack(next);
  };
  const onHideClosed = (e: ChangeEvent<HTMLInputElement>) => {
    const next = { ...value, hideClosed: e.target.checked };
    onChange(next);
    track("filter_hide_closed", { on: next.hideClosed });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <label className="flex items-center gap-2 text-zinc-400">
        Min $
        <input
          type="number"
          min="0"
          step="any"
          inputMode="decimal"
          value={value.minPriceUsd ?? ""}
          onChange={onMin}
          placeholder="0"
          className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-100 focus:outline-none focus:border-zinc-500"
        />
      </label>
      <label className="flex items-center gap-2 text-zinc-400">
        Max $
        <input
          type="number"
          min="0"
          step="any"
          inputMode="decimal"
          value={value.maxPriceUsd ?? ""}
          onChange={onMax}
          placeholder="∞"
          className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-100 focus:outline-none focus:border-zinc-500"
        />
      </label>
      <label className="flex items-center gap-2 text-zinc-400 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={value.hideClosed}
          onChange={onHideClosed}
          className="accent-emerald-500"
        />
        hide sold/cancelled
      </label>
    </div>
  );
}
